// ============================================================
// Auth Service
// ============================================================

import { mockUsers } from '@/mock/users';
import { MOCK_DELAY } from '@/constants/app';
import { authApi } from '@/api/super-admin/auth.api';

const delay = (ms = MOCK_DELAY.MEDIUM) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const authService = {
  login: async (email, password) => {
    const data = await authApi.login({ email, password });
    
    const fullName = [data.user.first_name, data.user.last_name].filter(Boolean).join(' ') || data.user.name || 'User';
    const roleUpper = String(data.user.role || '').toUpperCase();
    const qrScannerEnabled = data.qr_scanner_enabled ?? data.user?.qr_scanner_enabled ?? false;
    const user = { ...data.user, name: fullName, role: roleUpper, qr_scanner_enabled: qrScannerEnabled };
    const token = data.access_token;
    const modules = data.modules || [];
    
    let userSchools = [];
    if (roleUpper === 'SUPER_ADMIN' || roleUpper === 'ORGANIZATION_ADMIN') {
       userSchools = [];
    } else if (user.school?.id) {
       userSchools = [user.school.id];
    } else if (user.school_id) {
       userSchools = [user.school_id];
    }

    return {
      data: {
        token,
        user: {
          ...user,
          schools: userSchools,
        },
        modules,
        qr_scanner_enabled: qrScannerEnabled,
      },
    };
  },

  getCurrentUser: async (token) => {
    await delay(MOCK_DELAY.SHORT);
    if (!token) throw new Error('No token');
    
    // We don't have a /auth/me endpoint yet, and the JWT format changed.
    // Since Zustand persists the user object, we can recover it from localStorage.
    try {
      const stored = localStorage.getItem('erp_auth_token');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.state?.user) {
          // The persisted user should already have permissions/schools 
          // because it was saved during login()
          return { data: parsed.state.user };
        }
      }
    } catch (e) {
      console.error('Error recovering user from store:', e);
    }
    
    throw new Error('User not found');
  },

  logout: async () => {
    await delay(MOCK_DELAY.SHORT);
    return { data: { success: true } };
  },

  forgotPassword: async (email) => {
    await delay(MOCK_DELAY.MEDIUM);
    const user = mockUsers.find((u) => u.email === email);
    if (!user) {
      // Simulate silent failure for security
      return { data: { success: true } };
    }
    return { data: { success: true } };
  },

  resetPassword: async (payload) => {
    return await authApi.resetPassword(payload);
  },
};

export default authService;
